/**
 * Ctrl/Cmd+F find-in-doc for the active DocEditor.
 *
 * Claims the shortcut (capture) while a doc surface is open / focused so it
 * wins over mission search and the browser's native find.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  clearFindHighlight,
  findMatches,
  revealFindMatch,
  withOpenTogglesTo,
} from '../utils/docFind';
import { findScrollParent } from '../components/DocMinimap/utils/domUtils';

export function useDocFind(editorRootRef, blocks, blocksRef, commit) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const barRef = useRef(null);
  const activeIndexRef = useRef(0);
  activeIndexRef.current = activeIndex;
  const isOpenRef = useRef(false);
  isOpenRef.current = isOpen;

  const matches = useMemo(() => findMatches(blocks || [], query), [blocks, query]);
  const matchesRef = useRef(matches);
  matchesRef.current = matches;

  const close = useCallback(() => {
    setIsOpen(false);
    clearFindHighlight();
  }, []);

  const open = useCallback(() => {
    setIsOpen(true);
  }, []);

  const paint = useCallback((index, list = matchesRef.current, scrollToMatch = false) => {
    if (!list.length) {
      clearFindHighlight();
      return;
    }
    const next = ((index % list.length) + list.length) % list.length;
    revealFindMatch(list[next], {
      retainFocusEl: inputRef.current,
      allMatches: list,
      activeIndex: next,
      scrollToMatch,
    });
  }, []);

  const goToMatch = useCallback(
    (index, list = matchesRef.current) => {
      if (!list.length) {
        clearFindHighlight();
        setActiveIndex(0);
        activeIndexRef.current = 0;
        return;
      }
      const next = ((index % list.length) + list.length) % list.length;
      setActiveIndex(next);
      activeIndexRef.current = next;
      const match = list[next];
      const opened = withOpenTogglesTo(blocksRef.current, match.blockId);
      if (opened !== blocksRef.current) {
        commit(opened);
      }
      requestAnimationFrame(() => paint(next, list, true));
    },
    [blocksRef, commit, paint]
  );

  const goNext = useCallback(() => {
    goToMatch(activeIndexRef.current + 1);
  }, [goToMatch]);

  const goPrev = useCallback(() => {
    goToMatch(activeIndexRef.current - 1);
  }, [goToMatch]);

  // Jump to first match when the query changes (not on every doc edit)
  const prevQueryRef = useRef(null);
  useEffect(() => {
    if (!isOpen) {
      prevQueryRef.current = null;
      return;
    }
    if (prevQueryRef.current === query) return;
    prevQueryRef.current = query;
    if (!query.trim()) {
      clearFindHighlight();
      setActiveIndex(0);
      activeIndexRef.current = 0;
      return;
    }
    goToMatch(0, findMatches(blocksRef.current || [], query));
  }, [query, isOpen, blocksRef, goToMatch]);

  // Keep the active hit in range if the match list shifts while open
  useEffect(() => {
    if (!isOpen || !query.trim()) return;
    if (!matches.length) {
      clearFindHighlight();
      return;
    }
    if (activeIndexRef.current >= matches.length) {
      goToMatch(0, matches);
    } else {
      paint(activeIndexRef.current, matches);
    }
  }, [matches, isOpen, query, goToMatch, paint]);

  // Reposition overlay highlights when the doc scrolls/resizes
  useEffect(() => {
    if (!isOpen || !query.trim()) return undefined;
    const root = editorRootRef.current;
    const scrollParent =
      root?.closest('.doc-fullpage__scroll, .doc-sheet__body, .library-doc-pane') ||
      findScrollParent(root) ||
      root;
    let raf = 0;
    const repaint = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (!matchesRef.current.length) return;
        paint(activeIndexRef.current, matchesRef.current);
      });
    };
    scrollParent?.addEventListener('scroll', repaint, { passive: true });
    window.addEventListener('resize', repaint);
    return () => {
      cancelAnimationFrame(raf);
      scrollParent?.removeEventListener('scroll', repaint);
      window.removeEventListener('resize', repaint);
    };
  }, [isOpen, query, editorRootRef, paint]);

  // Click outside the find bar closes it (bubble — buttons won't dismiss it)
  useEffect(() => {
    if (!isOpen) return undefined;
    const onPointerDown = (e) => {
      if (e.target.closest?.('.doc-find-bar')) return;
      close();
    };
    // Defer bind one tick so the opening Ctrl+F click/key doesn't instantly close
    const timer = window.setTimeout(() => {
      document.addEventListener('mousedown', onPointerDown);
      document.addEventListener('touchstart', onPointerDown);
    }, 0);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [isOpen, close]);

  useEffect(() => {
    const onKeyDown = (e) => {
      const root = editorRootRef.current;
      if (!root) return;

      const surface = root.closest(
        '.doc-sheet--open, .doc-fullpage, .library-doc-pane'
      );
      const active = document.activeElement;
      const focusInSurface =
        (surface && active && surface.contains(active)) ||
        (active && root.contains(active)) ||
        active === inputRef.current ||
        !!active?.closest?.('.doc-find-bar');
      const sheetOpen = !!root.closest('.doc-sheet--open');

      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'f') {
        if (!sheetOpen && !focusInSurface && !isOpenRef.current) return;
        e.preventDefault();
        e.stopPropagation();
        if (isOpenRef.current) {
          const scroller =
            findScrollParent(root) ||
            root.closest(
              '.doc-fullpage__scroll, .doc-sheet__body, .library-doc-pane'
            );
          const pinned = scroller ? scroller.scrollTop : null;
          inputRef.current?.focus({ preventScroll: true });
          inputRef.current?.select();
          if (scroller != null && pinned != null && scroller.scrollTop !== pinned) {
            scroller.scrollTop = pinned;
          }
        } else {
          open();
        }
        return;
      }

      if (!isOpenRef.current) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }

      const inFindInput = active === inputRef.current;
      if (e.key === 'Enter' && inFindInput) {
        e.preventDefault();
        if (e.shiftKey) goPrev();
        else goNext();
        return;
      }

      if (e.key === 'F3') {
        e.preventDefault();
        if (e.shiftKey) goPrev();
        else goNext();
      }
    };

    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [editorRootRef, open, close, goNext, goPrev]);

  useEffect(() => () => clearFindHighlight(), []);

  return {
    isOpen,
    query,
    setQuery,
    matches,
    activeIndex,
    inputRef,
    barRef,
    open,
    close,
    goNext,
    goPrev,
  };
}
